import { useMemo, useState } from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import { useLanguage } from "../../../../context/LanguageContext";
import featureImageMap from "../../../../constants/featureMedia";
import { Colors } from "../../../../styles/theme";
import styles from "./FeaturesList.styles";
import Image from "../../../../components/Image";

const resolveFeatureImage = (sourceKey, featureKey) => {
  const imageName = featureImageMap[sourceKey]?.[featureKey];
  return imageName === "general-ledger.png"
    ? require("../../../../assets/general-ledger.webp")
    : imageName ? require(`../../../../assets/${imageName.replace(/\.png$/, "")}.png`) : null;
};

const FeaturesList = () => {
  const { translate, translations, selectedLanguage } = useLanguage();
  const isRTL = selectedLanguage === "ar";
  const [activeGroupKey, setActiveGroupKey] = useState("financial");
  const [activeFeatureKey, setActiveFeatureKey] = useState("general ledger");

  const featureGroups = useMemo(() => {
    const financial = translations?.["core financial management"] || [];
    const transactions =
      translations?.["transaction & inventory management"] || [];
    const reporting = translations?.["reporting features"] || [];
    const control = translations?.["control features"] || [];

    return [
      {
        key: "financial",
        label: translate("feature group financial"),
        sourceKey: "core financial management",
        items: financial,
      },
      {
        key: "transactions",
        label: translate("feature group transactions"),
        sourceKey: "transaction & inventory management",
        items: transactions,
      },
      {
        key: "reporting",
        label: translate("feature group reporting"),
        sourceKey: "reporting features",
        items: reporting,
      },
      {
        key: "control",
        label: translate("feature group control"),
        sourceKey: "control features",
        items: control,
      },
    ];
  }, [translate, translations]);

  const activeGroup =
    featureGroups.find((group) => group.key === activeGroupKey) ||
    featureGroups[0];
  const activeFeature =
    activeGroup.items.find((feature) => feature.key === activeFeatureKey) ||
    activeGroup.items[0];
  const activeImage = activeFeature
    ? resolveFeatureImage(activeGroup.sourceKey, activeFeature.key)
    : null;

  const isLedger = activeGroup.sourceKey === "core financial management" && activeFeature?.key === "general ledger";

  const selectGroup = (group) => {
    setActiveGroupKey(group.key);
    setActiveFeatureKey(group.items[0]?.key || "");
  };

  return (
    <Box
      component="section"
      className="section"
      id="features"
      sx={{
        backgroundColor: "#EEF3FA",
        direction: isRTL ? "rtl" : "ltr",
        scrollMarginTop: "80px",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: "1200px" }}>
        <Typography
          variant="subtitle2"
          component="p"
          color={Colors.primary}
          mb={{ xs: 1.25, sm: 2 }}
          sx={styles.eyebrow}
        >
          {translate("features eyebrow")}
        </Typography>
        <Typography variant="h2" sx={styles.heading} mt={0}>
          {translate("features header1")}
        </Typography>
        <Typography
          variant="h6"
          component="p"
          color={Colors.text}
          mt={{ xs: 1.5, sm: 2 }}
          sx={styles.description}
        >
          {translate("features header2")}
        </Typography>
      </Box>

      <Box sx={styles.explorer}>
        <Box
          role="tablist"
          aria-label={translate("features eyebrow")}
          sx={styles.tabs}
        >
          {featureGroups.map((group) => {
            const isActive = group.key === activeGroup.key;

            return (
              <ButtonBase
                key={group.key}
                component="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => selectGroup(group)}
                sx={{
                  ...styles.tab,
                  ...(isActive ? styles.tabActive : {}),
                }}
              >
                {group.label}
              </ButtonBase>
            );
          })}
        </Box>

        <Box sx={styles.explorerBody}>
          <Box component="nav" aria-label={activeGroup.label} sx={styles.featureNav}>
            {activeGroup.items.map((feature, index) => {
              const isActive = feature.key === activeFeature?.key;

              return (
                <ButtonBase
                  key={feature.key}
                  component="button"
                  onClick={() => setActiveFeatureKey(feature.key)}
                  sx={{
                    ...styles.featureButton,
                    ...(isActive ? styles.featureButtonActive : {}),
                  }}
                >
                  <Typography component="span" sx={styles.featureNumber}>
                    {String(index + 1).padStart(2, "0")}
                  </Typography>
                  <Typography component="span" sx={styles.featureButtonLabel}>
                    {feature.title}
                  </Typography>
                  <NorthEastIcon
                    aria-hidden="true"
                    sx={{
                      ...styles.featureArrow,
                      transform: isRTL ? "scaleX(-1)" : "none",
                    }}
                  />
                </ButtonBase>
              );
            })}
          </Box>

          {activeFeature && (
            <Box component="article" sx={styles.detailCard}>
              <Box sx={styles.imageStage}>
                {activeImage && (
                  <Image
                    src={activeImage}
                    srcSet={isLedger ? `${require("../../../../assets/general-ledger-small.webp")} 720w, ${require("../../../../assets/general-ledger.webp")} 1100w` : undefined}
                    sizes={isLedger ? "(max-width: 899px) calc(100vw - 58px), 65vw" : undefined}
                    loading="lazy"
                    decoding="async"
                    alt={translate("image alt feature screen").replace("{feature}", activeFeature.title)}
                    sx={styles.productImage}
                  />
                )}
              </Box>
              <Box sx={styles.detailContent}>
                <Typography sx={styles.detailEyebrow}>
                  {activeGroup.label}
                </Typography>
                <Typography variant="h3" sx={styles.detailTitle}>
                  {activeFeature.title}
                </Typography>
                <Box component="ul" sx={styles.pointsList}>
                  {activeFeature.points?.map((point) => (
                    <Typography
                      component="li"
                      key={point}
                      color={Colors.text}
                      sx={styles.point}
                    >
                      {point}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default FeaturesList;
